import { CommonModule, DOCUMENT, NgOptimizedImage } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  OnDestroy,
  Renderer2,
  inject,
} from '@angular/core';
import { HeaderBigImageLink } from 'www-cms';
import { BigImageLinkComponent } from './big-image-link/big-image-link.component';
import { MobileMenuComponent } from './mobile-menu/mobile-menu.component';
import { ButtonComponent } from 'ui-elements/button';
import { PromoBannerComponent } from './promo-banner/promo-banner.component';
import { Router, RouterLink } from '@angular/router';
import { HeaderActions, ShellState, ShellStateModel } from '../shell.state';
import { Store } from '@ngxs/store';
import { headerAnimations } from './header.animations';
import { TheaterShowsListComponent } from './theaterShowsList/theater-shows-list.component';
import {
  BehaviorSubject,
  Subject,
  combineLatest,
  map,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { OnInit } from '@angular/core';
import { A11yModule } from '@angular/cdk/a11y';
import { TicketingCartSelectors } from '../../ticketing/ticketing-cart.state';
import { GiftCardCart } from 'gift-cards';
import { toSignal } from '@angular/core/rxjs-interop';
import { PageSelectors } from '../../page/state/page.selectors';

@Component({
  selector: 'ss-www-header',
  imports: [
    CommonModule,
    MobileMenuComponent,
    TheaterShowsListComponent,
    BigImageLinkComponent,
    ButtonComponent,
    PromoBannerComponent,
    RouterLink,
    A11yModule,
    NgOptimizedImage,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: headerAnimations,
})
export class HeaderComponent implements OnInit, OnDestroy {
  private store = inject(Store);
  private router = inject(Router);
  private renderer = inject(Renderer2);
  private document = inject(DOCUMENT);
  shellState$ = this.store.select(ShellState.shellState);
  isMenuOrMobileMenuOpened$ = this.store.select(
    ShellState.isMenuOrMobileMenuOpened
  );
  headerLogo = this.store.selectSignal(PageSelectors.headerLogo);
  private ticketingCart$ = this.store
    .select(TicketingCartSelectors.getSeatLockExpiryDateTime)
    .pipe(map((seatLockExpiryDateTime) => !!seatLockExpiryDateTime));
  private giftCardCart$ = this.store
    .select(GiftCardCart.GiftCardCartSelectors.selectCalculationRequest)
    .pipe(
      map(
        (calculationRequest) => calculationRequest?.giftCardItems?.length ?? 0
      )
    );

  numberOfItemsInCart = toSignal(
    combineLatest([this.ticketingCart$, this.giftCardCart$]).pipe(
      map(([ticketingCart, giftCardCart]) => {
        return ticketingCart ? 1 : giftCardCart;
      })
    )
  );
  destroy$ = new Subject<void>();

  isMenuOrMobileMenuOpenedValue = new BehaviorSubject<boolean>(false);

  @Input() isTicketingPage = false;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (
      event.target.innerWidth > 1023 &&
      this.isMenuOrMobileMenuOpenedValue.value
    ) {
      this.dispatchSetMobileMenu(false);
    }
    if (
      event.target.innerWidth <= 1023 &&
      this.isMenuOrMobileMenuOpenedValue.value
    ) {
      this.dispatchSetMenu('none');
    }
  }

  ngOnInit(): void {
    this.dispatchSetMenu('none');
    this.isMenuOrMobileMenuOpened$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isOpen) => {
        this.isMenuOrMobileMenuOpenedValue.next(isOpen);
        if (isOpen) {
          this.renderer.addClass(this.document.body, 'no-scroll');
        } else {
          this.renderer.removeClass(this.document.body, 'no-scroll');
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackBigImageLink(index: number, item: HeaderBigImageLink) {
    return item.id;
  }

  closeAllMenus() {
    this.dispatchSetMenu('none');
    this.dispatchSetMobileMenu(false);
  }

  dispatchSetMenu(menu: ShellStateModel['openedMenu']) {
    this.store.dispatch(new HeaderActions.SetMenu(menu));
  }

  dispatchSetMobileMenu(isOpen: boolean) {
    this.store.dispatch(new HeaderActions.SetMobileMenu(isOpen));
    if (!isOpen) {
      this.store.dispatch(new HeaderActions.SetOpenedMobileMenu('main'));
    }
  }

  goToCart() {
    this.closeAllMenus();
    (this.ticketingCart$,
      this.giftCardCart$
        .pipe(
          take(1),
          switchMap((giftCardCart) => {
            if (giftCardCart) {
              return this.router.navigate(['/gift-cards/cart']);
            }
            return this.router.navigate(['/ticketing/cart']);
          }),
          takeUntil(this.destroy$)
        )
        .subscribe());
  }
}
